import { useCallback, useEffect, useRef, useState } from "react";
import { readAttachment, uploadAttachment } from "@/features/intelligence/api/client";
import {
  ATTACHMENT_TIMEOUT_MS,
  attachmentKey,
  hasUploadsInFlight,
  localRefusal,
  readyAttachmentIds,
  readyAttachments,
  type PendingAttachment,
} from "@/features/intelligence/lib/attachments";

export function useAttachmentController({
  activeId,
  activeIdRef,
  ensureConversation,
}: {
  activeId: string | null;
  activeIdRef: { current: string | null };
  ensureConversation: () => Promise<string | null>;
}) {
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const attachmentsRef = useRef<PendingAttachment[]>(attachments);
  const ownersRef = useRef(new Map<string, string | null>());
  const uploadsRef = useRef(new Map<string, AbortController>());
  const aliveRef = useRef(true);
  attachmentsRef.current = attachments;

  const updateAttachments = useCallback(
    (update: (previous: PendingAttachment[]) => PendingAttachment[]) => {
      setAttachments((previous) => {
        const next = update(previous);
        attachmentsRef.current = next;
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    for (const [key, owner] of ownersRef.current) {
      if (owner === activeId) continue;
      uploadsRef.current.get(key)?.abort();
      uploadsRef.current.delete(key);
      ownersRef.current.delete(key);
    }
    updateAttachments((previous) =>
      previous.filter((attachment) => ownersRef.current.get(attachment.key) === activeId),
    );
  }, [activeId, updateAttachments]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      for (const upload of uploadsRef.current.values()) upload.abort();
      uploadsRef.current.clear();
      ownersRef.current.clear();
      attachmentsRef.current = [];
    };
  }, []);

  const attach = useCallback(
    async (file: File): Promise<boolean> => {
      const key = attachmentKey();
      const ownerAtStart = activeIdRef.current;
      const base: PendingAttachment = {
        key,
        id: "",
        name: file.name || "upload",
        size: file.size,
        mimeType: file.type,
        status: "uploading",
      };
      const refusal = localRefusal(file);
      if (refusal) {
        ownersRef.current.set(key, activeIdRef.current);
        updateAttachments((previous) => [
          ...previous,
          { ...base, status: "failed", failure: refusal },
        ]);
        return false;
      }

      const id = await ensureConversation();
      if (!aliveRef.current) return false;
      if (!id) {
        if (activeIdRef.current !== ownerAtStart) return false;
        ownersRef.current.set(key, ownerAtStart);
        updateAttachments((previous) => [
          ...previous,
          { ...base, status: "failed", failure: "rejected" },
        ]);
        return false;
      }
      if (activeIdRef.current !== id) return false;

      if (ownerAtStart === null) {
        for (const [ownedKey, owner] of ownersRef.current) {
          if (owner === null) ownersRef.current.set(ownedKey, id);
        }
      }
      ownersRef.current.set(key, id);
      updateAttachments((previous) => [...previous, base]);

      const resolve = (patch: Partial<PendingAttachment>) => {
        if (!aliveRef.current || activeIdRef.current !== id || ownersRef.current.get(key) !== id)
          return;
        updateAttachments((previous) =>
          previous.map((attachment) =>
            attachment.key === key ? { ...attachment, ...patch } : attachment,
          ),
        );
      };

      const timer = new AbortController();
      uploadsRef.current.set(key, timer);
      const timeout = setTimeout(() => timer.abort(), ATTACHMENT_TIMEOUT_MS);
      try {
        const result = await uploadAttachment(id, file, timer.signal);
        if (!result.ok) {
          resolve({
            status: "failed",
            failure: "rejected",
            status_code: result.error.status,
          });
          return false;
        }
        if (activeIdRef.current !== id || ownersRef.current.get(key) !== id) return false;
        resolve({
          status: "ready",
          id: result.value.id,
          name: result.value.filename || base.name,
          size: result.value.size || base.size,
          mimeType: result.value.mimeType || base.mimeType,
        });

        try {
          const reconciled = await readAttachment(id, result.value.id, timer.signal);
          if (reconciled.ok && reconciled.value.filename) {
            resolve({
              name: reconciled.value.filename,
              size: reconciled.value.size || base.size,
              mimeType: reconciled.value.mimeType || base.mimeType,
            });
          }
        } catch {}
        return true;
      } catch {
        resolve({ status: "failed", failure: "timeout" });
        return false;
      } finally {
        clearTimeout(timeout);
        if (uploadsRef.current.get(key) === timer) uploadsRef.current.delete(key);
      }
    },
    [activeIdRef, ensureConversation, updateAttachments],
  );

  const removeAttachment = useCallback(
    (key: string) => {
      uploadsRef.current.get(key)?.abort();
      uploadsRef.current.delete(key);
      ownersRef.current.delete(key);
      updateAttachments((previous) => previous.filter((attachment) => attachment.key !== key));
    },
    [updateAttachments],
  );

  const takeReady = useCallback(() => {
    const id = activeIdRef.current;
    const current = attachmentsRef.current.filter(
      (attachment) => ownersRef.current.get(attachment.key) === id,
    );
    const sent = readyAttachments(current);
    const attachmentIds = readyAttachmentIds(current);
    for (const attachment of attachmentsRef.current) {
      uploadsRef.current.get(attachment.key)?.abort();
    }
    uploadsRef.current.clear();
    ownersRef.current.clear();
    attachmentsRef.current = [];
    setAttachments([]);
    return { sent, attachmentIds };
  }, [activeIdRef]);

  const uploadsInFlight = useCallback(() => {
    const id = activeIdRef.current;
    return hasUploadsInFlight(
      attachmentsRef.current.filter((attachment) => ownersRef.current.get(attachment.key) === id),
    );
  }, [activeIdRef]);

  const visibleAttachments = attachments.filter(
    (attachment) => ownersRef.current.get(attachment.key) === activeId,
  );

  return {
    attachments: visibleAttachments,
    attachmentsRef,
    attach,
    removeAttachment,
    hasUploadsInFlight: uploadsInFlight,
    takeReady,
  };
}
